import { NgClass, NgIf } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  OnChanges,
  inject,
} from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MatButtonToggleChange,
  MatButtonToggleModule,
} from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';

import { MatInputModule } from '@angular/material/input';
import {
  ButtonData,
  TableColumn,
  TableDataSource,
} from '../../../core/models/model';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatRadioModule } from '@angular/material/radio';
import { Router } from '@angular/router';
import { CreditCardItem } from '../../../core/models/card.model';
import { PaymentService } from '../../../core/services/payment.service';

@Component({
  selector: 'app-tab-grid',
  imports: [
    NgIf,
    NgClass,
    MatTableModule,
    MatIconModule,
    MatSortModule,
    MatCheckboxModule,
    MatButtonToggleModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    FormsModule,
    TranslateModule,
    MatRadioModule,
  ],
  templateUrl: './tab-grid.component.html',
  styleUrl: './tab-grid.component.scss',
})
export class TabGridComponent<T> implements OnInit, OnChanges {
  dateErrorKey = '';
  invalidDateMessage = '';
  router = inject(Router);
  paymentService = inject(PaymentService);
  startDate: Date | null = null;
  endDate: Date | null = null;
  @Input() columns: TableColumn<T>[] = []; //the column information
  // columns = input<TableColumn<T>[]>([]);
  // columns: Signal<TableColumn<T>[] | undefined> = input<TableColumn<T>[]>();
  @Input() tableData!: TableDataSource<T>; //the data which needs to be shown on the table
  @Input() buttonDetails!: ButtonData<T>[]; //button JSON if needed
  @Input() enabledDatePicker = false;
  @Input() isEditRow = false;
  @Input() isResetPasswordPage = false;
  @Output() redirectHandler = new EventEmitter<T>(); //redirect click function for navigating entities
  @Output() handleActionClick = new EventEmitter<T>(); //click function for action clicks
  @Output() isRowSelected = new EventEmitter<T>();
  @Output() datesSelected = new EventEmitter<{ start: string; end: string }>();
  @Output() defaultCreditCardChange = new EventEmitter<T>();

  @Input() set filterDates(dates: { start: string; end: string } | null) {
    if (dates) {
       const [year, month, day] = dates.start.split('-').map(Number);
      this.startDate = new Date(year, month - 1, day); // local date, no UTC shift
      const [year2, month2, day2] = dates.end.split('-').map(Number);
      this.endDate = new Date(year2, month2 - 1, day2); // local date, no UTC shift
    } else {
      this.startDate = null;
      this.endDate = null;
    }
  }

  totalItems!: number;
  pageSize = 10;
  currentPage = 0;
  totalPages!: number;
  pagedData!: T[];
  allData!: T[];
  sortedData: T[] = [];
  tableDataCopy!: TableDataSource<T>;
  displayedColumns: string[] = [];
  currentTab = '';
  dataSource!: TableDataSource<T>;
  today = new Date();
  selectedCardId: string | null = null;
  ngOnInit(): void {
    if (this.isResetPasswordPage) {
      this.pageSize = 5;
    }
    this.currentPage = 0;
    this.displayedColumns =
      this.columns.map((column: TableColumn<T>) => column.columnDef) ?? [];
    this.dataSource = this.tableData; //else just load the data
    if (this.router?.url?.includes('addcard')) {
      const defaultCard = this.dataSource.data.find(
        (card) => (card as CreditCardItem).CCDEF?._text === 'X'
      );
      this.selectedCardId =
        (defaultCard as CreditCardItem)?.CCARD_ID?._text || null;
    }
    if (!this.buttonDetails) {
      this.allData = this.tableData.data;
      this.sortedData = [...this.allData];
      this.totalPages = Math.ceil(this.sortedData.length / this.pageSize);
    }
    this.setPage(0);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes['buttonDetails']?.currentValue) {
      this.buttonDetails = changes['buttonDetails']?.currentValue;
      if (this.buttonDetails.length) {
        //if button details are passed then we need to set defaultTab onload
        this.setDefaultTab();
      } else {
        this.dataSource = this.tableData; //else just load the data
        this.totalPages = Math.ceil(this.tableData.data.length / this.pageSize);
        this.setPage(0);
      }
    }
  }
  /**
   * function sets the default tab based on the buttonjson passed
   * @actions if buttonjson is passed it checks for default field as true & sets the styles accordingly,
   * also loads the grid for the specified button selected
   */
  setDefaultTab() {
    //function to set the default tab which gets selected on load
    const defaultButton = this.buttonDetails.find(
      (button: ButtonData<T>) => button.default
    );
    if (defaultButton) {
      this.currentTab = defaultButton.buttonValue;
      this.loadDataSource();
    }
  }
  /**
   * function to handle the redirection of differnt entity
   * @action emit output event back to the component which handles the redirection
   */
  redirectEntity(entity: T) {
    this.redirectHandler.emit(entity);
  }
  /**
   * function to sort data
   * @returns sorted data in ascending/descending format
   */
  sortData(sort: Sort) {
    const data = this.allData.slice(); // always work on original full data
    if (!sort.active || sort.direction === '') {
      this.sortedData = this.allData; //restoring the original array
    } else {
      this.sortedData = data.sort((a: T, b: T) => {
        const aValue = this.getValueByKey(a, sort.active);
        const bValue = this.getValueByKey(b, sort.active);
        // const aStr = aValue as string | number;
        // const bStr = bValue as string | number;

        // return (aStr < bStr ? -1 : 1) * (sort.direction === 'asc' ? 1 : -1);
      const aDate = this.tryParseDate(aValue);
      const bDate = this.tryParseDate(bValue);

      let comparison = 0;

      if (aDate && bDate) {
        // Both are valid dates
        comparison = aDate.getTime() - bDate.getTime();
      } else {
        // Fallback to string/number comparison
        const aStr = aValue as string | number;
        const bStr = bValue as string | number;
        comparison = aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      }
      return comparison * (sort.direction === 'asc' ? 1 : -1);
      });
    }

    this.setPage(this.currentPage); // Refresh current page from sorted data
  }
  /**
   * function used to know if the value passed is a date field
   * @action returns date if the value passed is a geniune date type else return null
   */
  tryParseDate(value: unknown): Date | null {
  if (typeof value !== 'string') return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
  }
  /**
   *  This utility avoids using `any` and satisfies TypeScript + ESLint strict typing rules.
   */
  getValueByKey<T>(obj: T, key: string): unknown {
    return (obj as Record<string, unknown>)[key];
  }
  /**
   * function for few tables which has a clickable status or clickable action it
   *  will throw output to the parent
   */
  actionButtonClick(entity: T) {
    this.handleActionClick.emit(entity);
  }
  /**
   * click event to toggle the tabs & load the data based on the clicked tab
   */
  changeButtons(event: MatButtonToggleChange) {
    //function for toggling between buttons & changing the datasource as per the button
    if (event.value !== this.currentTab) {
      this.currentTab = event.value ?? '';
      this.loadDataSource();
    }
  }
  /**
   * function to load the definite data in grid based on the tab clicked
   * @returns data to be loaded in the grid
   */
  loadDataSource() {
    //loading the proper datasource ont he table wrt the button selected
    const selectedButton = this.buttonDetails.find(
      (button: ButtonData<T>) => button.buttonValue === this.currentTab
    );
    if (selectedButton) {
      this.allData = selectedButton.dataSource;
      this.dataSource = {
        data: selectedButton.dataSource,
      };
      this.sortedData = [...selectedButton.dataSource];
    }
    this.totalPages = Math.ceil(this.sortedData.length / this.pageSize);
    this.setPage(0);
  }
  /**
   * function to set the page number & specifies the no of entries per page
   */
  setPage(pageIndex: number) {
    this.currentPage = pageIndex;

    const start = this.currentPage * this.pageSize;
    const end = start + this.pageSize;

    this.totalItems = this.sortedData?.length || 0;
    this.pagedData = this.sortedData.slice(start, end);
    this.dataSource = {
      data: this.pagedData,
    };
  }


  /**
   * Handles raw input for start date field (string from input box)
   */
  onStartDateInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    const parsed = new Date(value);
    if (!value || isNaN(parsed.getTime())) {
      this.startDate = null;
      this.dateErrorKey = 'ticketHistory.invalidDateFormatError';
      this.invalidDateMessage = 'ticketHistory.invalidDateFormatError';
      return;
    }
    this.startDate = parsed;
    this.emitDatesIfValid();
  }

  /**
   * Handles raw input for end date field (string from input box)
   */
  onEndDateInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    const parsed = new Date(value);
    if (!value || isNaN(parsed.getTime())) {
      this.endDate = null;
      this.dateErrorKey = 'ticketHistory.invalidDateFormatError';
      this.invalidDateMessage = 'ticketHistory.invalidDateFormatError';
      return;
    }
    this.endDate = parsed;
    this.emitDatesIfValid();
  }

  /**
   * function gets called when start date is entered
   *  @action sets the selected date to the startDate property
   */
  onStartDateChange(date: Date | null) {
    if (!date) {
      this.startDate = null;
      this.dateErrorKey = 'ticketHistory.invalidDateFormatError';
      this.invalidDateMessage = 'ticketHistory.invalidDateFormatError';
      return;
    }
    this.startDate = date;
    this.emitDatesIfValid();
  }
  /**
   * function gets called when end date is entered
   *  @action sets the selected date to the endDate property
   */
  onEndDateChange(date: Date | null) {
    if (!date) {
      this.endDate = null;
      this.dateErrorKey = 'ticketHistory.invalidDateFormatError';
      this.invalidDateMessage = 'ticketHistory.invalidDateFormatError';
      return;
    }
    this.endDate = date;
    this.emitDatesIfValid();
  }
  /**
   * function validates if both the start & end dates are entered by the user
   * @action once start & end dates are validated, it emits an output event back to the component to
   * reload the data
   */
  emitDatesIfValid() {
    this.dateErrorKey = '';
    this.invalidDateMessage = '';
    // Check if dates are valid Date objects
    if (!this.isValidDate(this.startDate) || !this.isValidDate(this.endDate)) {
      this.dateErrorKey = 'ticketHistory.invalidDateFormatError';
      this.invalidDateMessage = 'ticketHistory.invalidDateFormatError';
      return;
    }
    // Check if input matches M/D/YYYY format
    const mdyRegex = /^(1[0-2]|0?[1-9])\/([1-9]|0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    const startStr = this.formatDateMDY(this.startDate);
    const endStr = this.formatDateMDY(this.endDate);
    if (!mdyRegex.test(startStr) || !mdyRegex.test(endStr)) {
      this.dateErrorKey = 'ticketHistory.invalidDateFormatError';
      this.invalidDateMessage = 'ticketHistory.invalidDateFormatError';
      return;
    }
    if (this.startDate && this.endDate && this.endDate < this.startDate) {
      this.dateErrorKey = 'ticketHistory.endDateAfterStartError';
      this.invalidDateMessage = 'ticketHistory.endDateAfterStartError';
      return;
    }
    // If all checks pass, emit dates in YYYY-MM-DD format for backend
    this.datesSelected.emit({ start: this.formatDate(this.startDate), end: this.formatDate(this.endDate) });
  }
  /**
   * function validates if the date is a valid Date instance
   *  @returns true if valid else false
   */
  isValidDate(date: unknown): date is Date {
    return date instanceof Date && !isNaN(date.getTime());
  }
  /**
   * function returns the date in YYYY-MM-DD format
   * @returns date in YYYY-MM-DD format to be shown in the grid
   */
  formatDate(date: Date): string {
    // Format as YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * function returns the date in M/D/YYYY format
   * @returns date in M/D/YYYY format
   */
  formatDateMDY(date: Date): string {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }
  /**
   * Filter for start date picker: disable dates after endDate
   * @returns true if endate >startdate or else false
   */
  startDateFilter = (date: Date | null): boolean => {
    if (!date) return false;
    return !this.endDate || date <= this.endDate;
  };
  /**
   * Filter for start date picker: disable dates after endDate
   * @returns true if endate >startdate or else false
   */
  endDateFilter = (date: Date | null): boolean => {
    if (!date) return false;
    return !this.startDate || date >= this.startDate;
  };
  /**
   * returns the entity which is clicked upon
   */
  onSelectedRow(row: T) {
    this.isRowSelected.emit(row);
  }
   /**
   * function sets the creditcard which is selected for edit from grid
   * @action it updates the signal in paymentservice & the updated creditcard is received in add-card component
   */
  prepopulateCreditCardDetails(cardObj: CreditCardItem) {
    this.paymentService.updateCreditCard('edit', cardObj);
  }
  defaultCardSelection(row: T) {
    this.defaultCreditCardChange.emit(row);
  }
}
